import { type DealQueueRow, type DealStage } from '@carsfinders/contracts';
import { Link } from '@tanstack/react-router';

import { t } from '../../../lib/i18n';
import { AUCTION_END_TONE_CLASS, type AuctionEnd, auctionEndOf, cn, formatAmount } from '../../bids/utils';
import { formatDayMonth, overdueDays } from '../../finance/utils';
import { DealNextAction, type DealNextActionProps } from './deal-next-action';

/**
 * `Лот · Клієнт·режим · Ставка · Етап·гроші · Наступна дія` — the artboard's
 * grid, verbatim, at a 1000px floor (the section scrolls sideways below it).
 */
export const DEAL_GRID =
  'grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_110px_minmax(0,1.5fr)_240px] gap-3.5 min-w-[1000px]';

/**
 * The «Наступна дія» column — header and body cell alike — pinned to the right
 * edge of the scrolling section (BUG-18): below the 1000px floor the table used
 * to scroll the action out of reach. `z-[1]` keeps the cell above the row-wide
 * stretched lot link, exactly as the old `relative z-[1]` did; `self-stretch` +
 * an opaque background hide whatever scrolls underneath, and the background
 * follows the row's hover tint through `group` so a stuck cell is not a hole.
 */
export const DEAL_ACTION_CELL =
  'sticky right-0 z-[1] self-stretch bg-cf-surface group-hover:bg-cf-surface-sunken';

interface StageStyle {
  /** Pill background + text. */
  pill: string;
  /** The 3px inset stripe on the row's left edge; empty = none. */
  stripe: string;
}

/**
 * The artboard's `STG` map, expressed in `cf-*` tokens only. Every colour in
 * the artboard's map is one of the tokens already (`#b26d00` = `cf-ok`,
 * `rgba(244,161,29,.16)` = `cf-ok-tint`, …), so nothing new is minted.
 * `payment_checking` is not on the artboard; it wears «Чекає оплати»'s colours
 * because it is the same wait with the ball in the operator's court.
 */
export const STAGE_STYLE: Record<DealStage, StageStyle> = {
  relay: {
    pill: 'bg-cf-ok-tint text-cf-ok',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-accent)]',
  },
  leading: { pill: 'bg-cf-ok-tint text-cf-ok', stripe: '' },
  won_uninvoiced: {
    pill: 'bg-cf-brand-tint text-cf-brand',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-brand)]',
  },
  awaiting_payment: {
    pill: 'bg-cf-warn-tint text-cf-warn',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-accent)]',
  },
  payment_checking: {
    pill: 'bg-cf-warn-tint text-cf-warn',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-accent)]',
  },
  payment_overdue: {
    pill: 'bg-cf-danger-tint text-cf-danger',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-danger)]',
  },
  shipping: {
    pill: 'bg-cf-info-tint text-cf-info-ink',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-info)]',
  },
  handed_over: { pill: 'bg-cf-success-tint text-cf-success', stripe: '' },
  outbid: { pill: 'bg-cf-fill text-cf-ink-subtle', stripe: '' },
  lost: { pill: 'bg-cf-fill text-cf-ink-subtle', stripe: '' },
  // Owner decision (a): the client refused to pay and the deposit was withheld.
  // Closed like «Видано», but in danger colours so it is not read as a sale.
  forfeited: { pill: 'bg-cf-danger-tint text-cf-danger', stripe: '' },
  // BUG-07: the win went to another bid. Muted like «Програно» — nothing is
  // owed and nothing is left to do — but its own caption says why.
  voided: { pill: 'bg-cf-fill text-cf-ink-subtle', stripe: '' },
  // local-ledger bridge: an admin's own win, now a card in the owners' book.
  // The platform's part is over — no stripe — in the info blue the ledger's
  // own «платформа» badge wears.
  in_ledger: { pill: 'bg-cf-info-tint text-cf-info-ink', stripe: '' },
};

/**
 * The stage pill's caption; the shipping one carries the step («Доставка · 2/4»).
 * A bid waiting to be placed reads «Не оброблено» (owner, 2026-10-26: not
 * «Ретрансляція») — or «Оброблено» when an older bid was marked by hand
 * without being relayed.
 */
export function dealStageLabel(
  row: Pick<DealQueueRow, 'stage' | 'shipStep' | 'processedOnAuction'>,
): string {
  if (row.stage === 'relay' && row.processedOnAuction === true) {
    return t('admin.deals.stage.relayProcessed');
  }
  if (row.stage === 'shipping') {
    return t('admin.deals.stage.shipping', { step: String((row.shipStep ?? 0) + 1) });
  }
  return t(`admin.deals.stage.${row.stage}`);
}

/** «депозит ×10», «довірений · 10», or «режим не задано» for a client never backfilled. */
export function dealModeLabel(row: Pick<DealQueueRow, 'clientMode'>): string {
  const mode = row.clientMode;
  if (!mode) return t('admin.deals.mode.none');
  if (mode.mode === 'trusted') {
    return t('admin.deals.mode.trusted', { n: String(mode.bidLimit ?? '—') });
  }
  return t('admin.deals.mode.deposit', { n: String(mode.multiplier ?? '—') });
}

/**
 * The money line under the pill — what the operator needs to say to the client
 * at this stage, from the row's own facts only:
 * «CF-2609-014 · CHF 17 300 до 29.09», «Забір з аукціону · далі: транспорт до
 * львова», «сума звільнилась».
 *
 * Every parameter is resolved to a literal before `t` sees it — an undefined
 * one would print the raw `{token}`.
 */
export function dealMoneyLine(row: DealQueueRow, now: Date = new Date()): string {
  const since = formatDayMonth(row.stageEnteredAt);
  switch (row.stage) {
    case 'relay':
      return t('admin.deals.money.relay', { date: since });
    case 'leading':
      return t('admin.deals.money.leading');
    case 'won_uninvoiced':
      // An admin's own win is never invoiced — it goes to the local ledger.
      return t(
        row.nextAction === 'add_to_ledger'
          ? 'admin.deals.money.wonOwn'
          : 'admin.deals.money.wonUninvoiced',
      );
    case 'in_ledger':
      return t('admin.deals.money.inLedger', { date: since });
    case 'awaiting_payment':
    case 'payment_checking':
    case 'payment_overdue': {
      const invoice = row.invoice;
      if (!invoice) return t('admin.deals.money.noInvoice');
      if (row.stage === 'payment_checking') {
        return t('admin.deals.money.invoiceChecking', { no: invoice.no });
      }
      if (row.stage === 'payment_overdue') {
        return t('admin.deals.money.invoiceOverdue', {
          no: invoice.no,
          amount: formatAmount(invoice.dueChf),
          days: String(overdueDays(invoice.dueAt, now)),
        });
      }
      return t('admin.deals.money.invoiceDue', {
        no: invoice.no,
        amount: formatAmount(invoice.dueChf),
        date: formatDayMonth(invoice.dueAt),
      });
    }
    case 'shipping': {
      const step = row.shipStep ?? 0;
      const current = t(`admin.deals.ship.${step}`);
      if (step >= 3) return t('admin.deals.money.shipLast', { step: current });
      return t('admin.deals.money.shipNext', {
        step: current,
        next: t(`admin.deals.ship.${step + 1}`).toLowerCase(),
      });
    }
    case 'handed_over':
      return t('admin.deals.money.handedOver', { date: since });
    case 'forfeited':
      return t('admin.deals.money.forfeited', { date: since });
    case 'voided':
      return t('admin.deals.money.voided', { date: since });
    case 'lost':
    case 'outbid':
      return t('admin.deals.money.lost');
  }
}

/**
 * Stages whose outcome still waits on the real auction — only there is its
 * close the operator's deadline. Past it (a deal, shipping, …) the line is noise.
 */
const AUCTION_END_STAGES: ReadonlySet<DealStage> = new Set(['relay', 'leading']);

/**
 * Row marking by the lot's auction close, over the stage's own stripe (owner,
 * 2026-10-13: «помічати рядки вертикальною кольоровою лінією — завершені
 * ставки, ставки які скоро завершуються»). Thicker than a stage stripe so it
 * reads first:
 *  - ended — the auction is over and the outcome is not in: the operator's
 *    next move is «Виграно / Не виграно». Blue + tint, the same blue the
 *    client's «awaiting the result» card wears;
 *  - soon — closes within a day (`auctionEndOf`'s threshold): amber;
 *  - open — nothing to add; the stage stripe stays.
 */
export const AUCTION_END_ROW_CLASS: Record<AuctionEnd['tone'], string> = {
  ended: 'bg-cf-info-tint shadow-[inset_5px_0_0_var(--color-cf-info)]',
  soon: 'shadow-[inset_5px_0_0_var(--color-cf-warn)]',
  open: '',
};

/**
 * The same tint on the sticky «Наступна дія» cell, which paints its own opaque
 * surface so a sideways-scrolled row never shows through it — and therefore
 * hid the row's blue behind white (owner, 2026-10-13). The tint goes on as a
 * background IMAGE over that surface: the colour still matches the row, and
 * the cell stays opaque. On hover the row drops to the grey sunken surface
 * (`hover:` beats the row's tint), so the image must drop with it — otherwise
 * the cell stayed blue beside a grey row (owner, 2026-10-13).
 */
export const AUCTION_END_ACTION_CELL_CLASS: Record<AuctionEnd['tone'], string> = {
  ended:
    'bg-[image:linear-gradient(var(--color-cf-info-tint),var(--color-cf-info-tint))] group-hover:bg-none',
  soon: '',
  open: '',
};

/** `Лот 1A2F · AXA · 28.09` — the grey second line under the car. */
function dealMetaLine(row: DealQueueRow): string {
  return [
    row.lotNumber ? t('admin.deals.lot', { lot: row.lotNumber }) : null,
    formatDayMonth(row.stageEnteredAt),
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

interface DealRowProps {
  row: DealQueueRow;
  onIssueInvoice: DealNextActionProps['onIssueInvoice'];
  now?: Date;
}

/**
 * ONE client lot on the «Угоди» screen. The lot links to its bid history
 * (`/bids/$id` stays — the old «Ставки» detail is where every bid on a lot is
 * inspectable) and the WHOLE row opens it — the client cell is plain text since
 * 2026-10-12, so no click on the row lands on the client card instead. The last
 * cell is the ONE next action the server resolved.
 *
 * `data-testid` / `data-bid-id` / `data-listing-id` are the test seam the
 * relay-desk rows carried, so the e2e journeys can address a row here too.
 */
export function DealRow({ row, onIssueInvoice, now }: DealRowProps) {
  const style = STAGE_STYLE[row.stage];
  const lotLabel = row.lotNumber ?? row.carTitle;
  const overdue = row.stage === 'payment_overdue';
  const auctionEnd = AUCTION_END_STAGES.has(row.stage)
    ? auctionEndOf(row.auctionEndAt, now?.getTime())
    : null;
  const auctionEndRow = auctionEnd ? AUCTION_END_ROW_CLASS[auctionEnd.tone] : '';

  // The stage pill + money line. A bid still to be placed shows it in «Наступна
  // дія» instead, leaving «Етап · гроші» empty (owner, 2026-10-26).
  const stageBlock = (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={cn(
            'inline-flex rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase',
            style.pill,
          )}
        >
          {dealStageLabel(row)}
        </span>
        {row.slaOverdue && (
          <span className="font-mono text-[10px] tracking-[0.08em] text-cf-danger uppercase">
            {t('admin.deals.slaOverdue')}
          </span>
        )}
      </div>
      <div
        className={cn('mt-1 truncate text-xs', overdue ? 'text-cf-danger' : 'text-cf-ink-subtle')}
      >
        {dealMoneyLine(row, now)}
      </div>
    </>
  );
  const unplaced = row.stage === 'relay';

  return (
    <div
      role="row"
      data-testid="deal-row"
      data-bid-id={row.bidId}
      data-listing-id={row.lotNumber ?? row.carId}
      data-stage={row.stage}
      data-auction-end-tone={auctionEnd?.tone}
      className={cn(
        DEAL_GRID,
        // `relative` anchors the lot link's stretched ::after — the WHOLE row
        // opens the bid page, the same pattern as the bids table row. `group`
        // lets the sticky action cell follow the row's hover tint.
        'group relative items-center border-b border-cf-line-soft px-[18px] py-3 text-[13px] leading-[normal] last:border-b-0 hover:bg-cf-surface-sunken',
        auctionEndRow || style.stripe,
      )}
    >
      <div role="cell" className="flex min-w-0 items-center gap-2.5">
        {row.imageUrl ? (
          <img
            src={row.imageUrl}
            alt=""
            loading="lazy"
            className="h-10 w-[60px] shrink-0 rounded-cf-sm bg-cf-fill object-cover"
          />
        ) : (
          <span aria-hidden className="h-10 w-[60px] shrink-0 rounded-cf-sm bg-cf-fill" />
        )}
        <div className="min-w-0">
          <Link
            to="/bids/$id"
            params={{ id: row.bidId }}
            aria-label={t('admin.deals.lotHistory', { lot: lotLabel })}
            data-testid="deal-row-link"
            // BUG-18: the title truncates in the 1.5fr column — the hint keeps it whole.
            title={row.carTitle}
            // The row-wide navigation target: a real <a> whose ::after is
            // stretched over the row, so Cmd/Ctrl/middle-click still work. The
            // client link (`relative z-[1]`) and the next-action cell (`sticky
            // z-[1]`) sit above it and keep their own clicks — no nested
            // interactive elements.
            className="block truncate font-semibold text-cf-ink after:absolute after:inset-0 after:content-[''] hover:underline focus-visible:outline-2 focus-visible:outline-cf-brand"
          >
            {row.carTitle}
          </Link>
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-cf-ink-subtle">
            {/* The auction the lot is played on, first and as a chip — a long
                lot number truncates the rest of the line, never this (owner,
                2026-10-13: «бачити аукціон, на якому проводиться лот»). */}
            {row.auctionHouse && (
              <span
                data-testid="deal-row-auction"
                className="shrink-0 rounded-cf-sm bg-cf-fill px-1.5 py-px font-mono text-[10px] font-semibold tracking-[0.04em] text-cf-ink uppercase"
              >
                {row.auctionHouse}
              </span>
            )}
            <span className="truncate">{dealMetaLine(row)}</span>
          </div>
          {auctionEnd && (
            <div
              data-testid="deal-row-auction-end"
              data-tone={auctionEnd.tone}
              className={cn('truncate text-xs', AUCTION_END_TONE_CLASS[auctionEnd.tone])}
            >
              {auctionEnd.label}
            </div>
          )}
        </div>
      </div>

      {/* Plain text, not a link (owner, 2026-10-12: «клік по всьому рядку має
          переходити на деталі ставки, а не на сторінку клієнта»). The client's
          card is one step away — the «Клієнт» filter, or the bid page. */}
      <div role="cell" className="min-w-0" data-testid="deal-row-client">
        <span className="block truncate text-cf-ink" title={row.clientName}>
          {row.clientName}
        </span>
        <span
          className={cn(
            'block font-mono text-[11px]',
            row.clientMode?.mode === 'trusted' ? 'text-cf-ok' : 'text-cf-ink-muted',
          )}
        >
          {dealModeLabel(row)}
        </span>
      </div>

      <span role="cell" className="text-right font-mono text-sm font-medium whitespace-nowrap">
        CHF {formatAmount(row.bidChf)}
      </span>

      <div role="cell" className="min-w-0">
        {!unplaced && stageBlock}
      </div>

      <div
        role="cell"
        className={cn(
          DEAL_ACTION_CELL,
          auctionEnd && AUCTION_END_ACTION_CELL_CLASS[auctionEnd.tone],
          'flex min-w-0 flex-col justify-center',
        )}
      >
        {unplaced ? (
          <div data-testid="deal-row-unplaced" className="min-w-0">
            {stageBlock}
          </div>
        ) : (
          <DealNextAction row={row} onIssueInvoice={onIssueInvoice} now={now?.getTime()} />
        )}
      </div>
    </div>
  );
}
